import React, { useState, useEffect, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useAppStore } from '../../store/appStore';
import { notesApi } from '../../services/api';
import Avatar from '../common/Avatar';

// Per-camera Controller Notes history. Newest first; each row shows author, absolute
// + relative timestamp, the note text, and a snapshot thumbnail when present. Admins
// can delete; anyone can export the camera's notes to CSV. Notes are added from the
// floating overlay (ControllerNotesOverlay); `refreshKey` bumps to reload after a save.
export default function ControllerNotesTab({ camera, refreshKey }) {
  const { user, addToast } = useAppStore();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    try {
      const res = await notesApi.list(camera.id);
      setNotes(res.data.notes || []);
    } catch {
      addToast({ type: 'error', message: 'Failed to load notes' });
    } finally {
      setLoading(false);
    }
  }, [camera.id, addToast]);

  useEffect(() => { load(); }, [load, refreshKey]);

  async function deleteNote(note) {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    setDeletingId(note.id);
    try {
      await notesApi.remove(camera.id, note.id);
      setNotes(prev => prev.filter(n => n.id !== note.id));
      addToast({ type: 'success', message: 'Note deleted' });
    } catch (err) {
      addToast({ type: 'error', message: err.response?.data?.error || 'Failed to delete note' });
    } finally {
      setDeletingId(null);
    }
  }

  function exportCsv() {
    if (notes.length === 0) return;
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [['Timestamp', 'Author', 'Note', 'Snapshot URL']];
    // Chronological (oldest first) reads more naturally in a spreadsheet.
    [...notes].reverse().forEach(n => {
      const url = n.snapshotFilename ? `${window.location.origin}/api/snapshots/file/${n.snapshotFilename}` : '';
      rows.push([new Date(n.timestamp).toISOString(), n.username, n.text, url]);
    });
    const csv = rows.map(r => r.map(esc).join(',')).join('\r\n');
    const safeName = (camera.name || 'camera').replace(/[^a-zA-Z0-9_-]/g, '_');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}-controller-notes.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  return (
    <div style={{ marginTop: '4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <div className="section-title" style={{ flexShrink: 0 }}>Controller Notes ({notes.length})</div>
        <div style={{ flex: 1 }} />
        <button className="btn btn-ghost btn-sm" onClick={exportCsv} disabled={notes.length === 0} title="Export notes to CSV">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Export CSV
        </button>
      </div>

      {loading ? (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', padding: '24px' }}>
          Loading notes…
        </div>
      ) : notes.length === 0 ? (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', padding: '24px', lineHeight: 1.6, background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-dim)' }}>
          No notes yet.<br />
          Use the “＋ Note” button on the video to flag something interesting.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {notes.map(n => (
            <div key={n.id} style={{
              display: 'flex', gap: '12px', alignItems: 'flex-start',
              padding: '12px 14px',
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-dim)',
              borderRadius: 'var(--radius-md)',
            }}>
              <Avatar src={n.avatar} name={n.username} userId={n.userId} size={28} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {n.username}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-secondary)' }}>
                    {new Date(n.timestamp).toLocaleString()}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-muted)', opacity: 0.7 }}>
                    {formatDistanceToNow(new Date(n.timestamp), { addSuffix: true })}
                  </span>
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.45, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                  {n.text}
                </div>
                {n.snapshotFilename && (
                  <a href={`/api/snapshots/file/${n.snapshotFilename}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: '8px' }}>
                    <img
                      src={`/api/snapshots/file/${n.snapshotFilename}`}
                      alt="Note snapshot"
                      style={{ maxWidth: '220px', width: '100%', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-dim)', display: 'block' }}
                    />
                  </a>
                )}
              </div>
              {isAdmin && (
                <button
                  className="btn btn-ghost btn-sm btn-icon"
                  onClick={() => deleteNote(n)}
                  disabled={deletingId === n.id}
                  title="Delete note"
                  style={{ flexShrink: 0, color: 'var(--accent-red)' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
